/* ==========================================================================
   ORKA — carousel helper
   --------------------------------------------------------------------------
   Thin wrapper around Swiper that every Orka section shares.

   - Waits for the deferred Swiper bundle before initialising.
   - `onlyBelow` builds the carousel under a breakpoint and tears it down
     above it, so a section can be a plain grid on desktop and a swipeable
     peek carousel on mobile without fighting Swiper's own breakpoints.
   - Slide widths come from CSS (`--orka-slide-w` + slidesPerView: 'auto'),
     so per-section sizing stays in the stylesheet where it belongs.
   - Re-initialises on shopify:section:load / :unload for the theme editor.

   Usage:
     OrkaCarousel.create(rootEl, { … swiper options … }, { onlyBelow: 990 });
   ========================================================================== */

(function () {
  'use strict';

  var instances = [];

  function whenSwiperReady(cb) {
    if (window.Swiper) {
      cb();
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      if (window.Swiper) {
        clearInterval(timer);
        cb();
      } else if (++tries > 150) {
        clearInterval(timer);
        console.warn('[Orka] Swiper failed to load; carousels fall back to scroll.');
      }
    }, 60);
  }

  function Carousel(el, options, config) {
    this.el = el;
    this.options = options || {};
    this.config = config || {};
    this.swiper = null;

    this.mql = this.config.onlyBelow
      ? window.matchMedia('(max-width: ' + (this.config.onlyBelow - 1) + 'px)')
      : null;

    this.sync = this.sync.bind(this);

    whenSwiperReady(
      function () {
        this.sync();
        if (this.mql) {
          // Safari < 14 only supports addListener.
          if (this.mql.addEventListener) {
            this.mql.addEventListener('change', this.sync);
          } else {
            this.mql.addListener(this.sync);
          }
        }
      }.bind(this)
    );
  }

  Carousel.prototype.shouldRun = function () {
    return this.mql ? this.mql.matches : true;
  };

  Carousel.prototype.sync = function () {
    if (this.shouldRun()) {
      this.build();
    } else {
      this.teardown();
    }
  };

  /* A centred peek carousel shows the active slide plus a full neighbour on
     each side, so Swiper needs roughly 3 slides on screen at once. With only
     3 slides total it runs out of material and `loop` cannot close the ring —
     you get blank space at the ends instead of an infinite scroll.

     Padding the track with clones fixes that. They are added on build and
     stripped on teardown, because sections like the testimonials collapse to a
     plain grid on desktop where leftover duplicates would be visible. */
  Carousel.prototype.padSlides = function () {
    var min = this.config.minSlides || 0;
    if (!min) return;

    var wrapper = this.el.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    var originals = Array.prototype.filter.call(wrapper.children, function (n) {
      return n.classList && n.classList.contains('swiper-slide') && !n.dataset.orkaClone;
    });
    if (!originals.length || originals.length >= min) return;

    var i = 0;
    while (wrapper.querySelectorAll('.swiper-slide').length < min) {
      var clone = originals[i % originals.length].cloneNode(true);
      clone.dataset.orkaClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      // Duplicate ids and theme-editor hooks would confuse both the DOM and
      // the section editor, so strip them from copies.
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (n) {
        n.removeAttribute('id');
      });
      wrapper.appendChild(clone);
      i++;
    }
  };

  Carousel.prototype.unpadSlides = function () {
    var clones = this.el.querySelectorAll('.swiper-slide[data-orka-clone]');
    Array.prototype.forEach.call(clones, function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
  };

  Carousel.prototype.build = function () {
    if (this.swiper) return;

    this.padSlides();

    var defaults = {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      speed: 420,
      grabCursor: true,
      threshold: 4,
      watchOverflow: true,
      resizeObserver: true,
      observer: true,
      observeParents: true,
      // One slide per gesture — never free scroll.
      freeMode: false,
      longSwipesRatio: 0.25
    };

    var opts = Object.assign({}, defaults, this.options);
    this.swiper = new window.Swiper(this.el, opts);
    this.el.classList.add('is-carousel');

    if (typeof this.config.onReady === 'function') {
      this.config.onReady(this.swiper, this);
    }
  };

  Carousel.prototype.teardown = function () {
    if (!this.swiper) return;
    // Destroy first so Swiper removes its own loop duplicates, then drop ours.
    this.swiper.destroy(true, true);
    this.swiper = null;
    this.unpadSlides();
    this.el.classList.remove('is-carousel');
  };

  Carousel.prototype.destroy = function () {
    this.teardown();
    if (this.mql) {
      if (this.mql.removeEventListener) {
        this.mql.removeEventListener('change', this.sync);
      } else {
        this.mql.removeListener(this.sync);
      }
    }
  };

  window.OrkaCarousel = {
    // Run a callback once the Swiper bundle has finished loading.
    ready: whenSwiperReady,

    create: function (el, options, config) {
      if (!el) return null;
      var instance = new Carousel(el, options, config);
      instances.push(instance);
      return instance;
    },

    // Tear down every carousel inside a node — used when the theme editor
    // removes or re-renders a section.
    destroyWithin: function (root) {
      instances = instances.filter(function (instance) {
        if (root && root.contains && root.contains(instance.el)) {
          instance.destroy();
          return false;
        }
        return true;
      });
    }
  };

  document.addEventListener('shopify:section:unload', function (evt) {
    window.OrkaCarousel.destroyWithin(evt.target);
  });
})();
